import type { AgentConnection, Job } from '@cef-ai/vault-sdk';
import { displayAgentName } from '@/lib/vault/agent-display';

/**
 * Agent ids ordered newest-activity-first. An agent's activity time is the
 * later of its most-recent job and its own connection `updatedAt`/`createdAt`
 * (the API guarantees those on every AgentConnection), so an agent with no
 * jobs still sorts by when it was connected/changed rather than dropping to a
 * name-only bucket. Ties broken stably by display name asc. The roster
 * (sidebar + blocks) and the activity feed all sort off this same signal.
 */
export function computeRecencyOrder(agents: AgentConnection[], jobs: Job[]): string[] {
  const latestJob = new Map<string, string>();
  for (const job of jobs) {
    const id = job.agentId;
    if (!id) continue;
    const t = job.updatedAt ?? job.createdAt;
    const prev = latestJob.get(id);
    if (!prev || t > prev) latestJob.set(id, t);
  }
  const activity = (a: AgentConnection): string => {
    const jobT = latestJob.get(a.agentId) ?? '';
    const connT = a.updatedAt ?? a.createdAt ?? '';
    return jobT > connT ? jobT : connT;
  };
  return [...agents]
    .sort((a, b) => {
      const ta = activity(a);
      const tb = activity(b);
      if (ta !== tb) return ta < tb ? 1 : -1;
      return displayAgentName(a.agentId).localeCompare(displayAgentName(b.agentId));
    })
    .map((a) => a.agentId);
}
